/**
 * №11.2 §1.4 / 11.2.4 — the Firm-settings "Roles & access" panel. Makes the
 * security model legible + operable by the owner: the declared capability areas
 * (money-closed flagged), the workspace roles (a role = a named area set), who
 * holds which role (users AND agents — one actor model), and flat groups. Assign
 * a role or spin up a group inline (D-W2). Matter restriction lives on the matter
 * record page (restrict from where the matter is), not here.
 */
import { useState } from "react";
import { showToast } from "../../components/primitives";
import {
  useAreas, useRoles, useRoleAssignments, useGroups,
  useAssignRole, useUnassignRole, useCreateGroup,
} from "../../hooks/use-policy";

export function RolesAccessPanel() {
  const areas = useAreas();
  const roles = useRoles();
  const assignments = useRoleAssignments();
  const groups = useGroups();
  const assign = useAssignRole();
  const unassign = useUnassignRole();
  const createGroup = useCreateGroup();

  const [subjectKind, setSubjectKind] = useState("user");
  const [subjectId, setSubjectId] = useState("");
  const [roleName, setRoleName] = useState("");
  const [groupName, setGroupName] = useState("");

  const roleRows = roles.data ?? [];
  const areaRows = areas.data?.areas ?? [];

  const doAssign = () => {
    if (!subjectId.trim() || !roleName) { showToast("Pick a subject id and a role."); return; }
    assign.mutate({ subject_kind: subjectKind, subject_id: subjectId.trim(), role_name: roleName }, {
      onSuccess: () => { showToast(`Assigned ${roleName}.`); setSubjectId(""); },
      onError: (e) => showToast((e as Error).message),
    });
  };
  const doCreateGroup = () => {
    if (!groupName.trim()) return;
    createGroup.mutate({ name: groupName.trim(), members: [] }, {
      onSuccess: () => { showToast(`Group "${groupName}" created.`); setGroupName(""); },
      onError: (e) => showToast((e as Error).message),
    });
  };

  return (
    <div className="card card--accent">
      <div className="card__h">Roles &amp; access <span className="sub">who can do what · money is closed by default</span></div>

      {/* areas */}
      <div className="card__b">
        <div className="tiny muted" style={{ marginBottom: 6 }}>Capability areas (declared by installed modules)</div>
        <div className="actionbar" style={{ gap: 8, flexWrap: "wrap" }}>
          {areaRows.map((a) => (
            <span key={a.key} className={`badge ${a.money ? "badge--fixed" : "badge--soft"}`} title={a.description ?? a.key}>
              {a.key}{a.money ? " · money" : a.default_member ? " · default" : ""}
            </span>
          ))}
          {areaRows.length === 0 && <span className="muted tiny">No areas yet — install a module.</span>}
        </div>
      </div>

      {/* roles */}
      <div className="card__b">
        <div className="tiny muted" style={{ marginBottom: 6 }}>Roles (a role = a named set of areas)</div>
        <table className="ledger-table"><thead><tr><th>Role</th><th>Areas</th><th>Source</th></tr></thead><tbody>
          {roleRows.map((r) => (
            <tr key={r.name}><td className="primary">{r.name}</td><td className="tiny">{r.areas.join(", ")}</td><td>{r.seeded ? <span className="badge badge--soft">seeded</span> : <span className="badge badge--tm">custom</span>}</td></tr>
          ))}
          {roleRows.length === 0 && <tr><td colSpan={3} className="muted tiny">No roles yet.</td></tr>}
        </tbody></table>
      </div>

      {/* assign */}
      <div className="card__b">
        <div className="tiny muted" style={{ marginBottom: 6 }}>Assign a role (users AND agents hold roles identically)</div>
        <div className="actionbar" style={{ gap: 8, flexWrap: "wrap" }}>
          <select className="cem-input" value={subjectKind} onChange={(e) => setSubjectKind(e.target.value)} style={{ maxWidth: 120 }}>
            <option value="user">user</option>
            <option value="agent">agent</option>
          </select>
          <input className="cem-input" placeholder="user id / agent node id" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} style={{ minWidth: 220 }} />
          <select className="cem-input" value={roleName} onChange={(e) => setRoleName(e.target.value)} style={{ maxWidth: 200 }}>
            <option value="">— role —</option>
            {roleRows.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
          </select>
          <button data-variant="primary" disabled={assign.isPending} onClick={doAssign}>Assign</button>
        </div>
        <table className="ledger-table" style={{ marginTop: 10 }}><thead><tr><th>Subject</th><th>Kind</th><th>Role</th><th /></tr></thead><tbody>
          {(assignments.data ?? []).map((a) => (
            <tr key={`${a.subject_kind}:${a.subject_id}:${a.role_name}`}>
              <td className="tiny">{a.subject_id}</td><td><span className="badge badge--soft">{a.subject_kind}</span></td><td>{a.role_name}</td>
              <td style={{ textAlign: "right" }}><button className="linkish" style={{ border: "none", background: "none", cursor: "pointer", color: "var(--color-text-tertiary)", fontSize: 11 }}
                onClick={() => unassign.mutate({ subject_kind: a.subject_kind, subject_id: a.subject_id, role_name: a.role_name })}>Revoke</button></td>
            </tr>
          ))}
          {(assignments.data ?? []).length === 0 && <tr><td colSpan={4} className="muted tiny">No role assignments — every member holds the open-workspace defaults (matters.* + billing.view); money needs a role.</td></tr>}
        </tbody></table>
      </div>

      {/* groups */}
      <div className="card__b">
        <div className="tiny muted" style={{ marginBottom: 6 }}>Groups (flat · usable in role assignment + matter restriction)</div>
        <div className="actionbar" style={{ gap: 8 }}>
          <input className="cem-input" placeholder="new group name" value={groupName} onChange={(e) => setGroupName(e.target.value)} style={{ minWidth: 220 }} />
          <button disabled={createGroup.isPending} onClick={doCreateGroup}>Create group</button>
        </div>
        <div className="actionbar" style={{ gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          {(groups.data ?? []).map((g) => <span key={g.id} className="badge badge--soft" title={g.id}>{g.name} · {g.members.length}</span>)}
        </div>
      </div>
    </div>
  );
}
